import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Paper, Alert, Stack } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getBusinessConfigById } from '../../firebase/firebase-delegate';
import { BusinessConfig } from '../../types';
import { resolveAdminBookingUxConfig } from '../../utils/bookingUxConfig';
import { activateProvisionedTenant, getTenantPublishStatus } from '../../sitePublishing/api';
import OwnerInvitationCard from './OwnerInvitationCard';
import SalesPreviewCard from './SalesPreviewCard';
import TenantClosureSection from './TenantClosureSection';
import LoadingSpinner from '../../components/global/LoadingSpinner';

const ManageSitePage: React.FC = () => {
    const { siteId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isNewSite, setIsNewSite] = useState(false);
    const [checking, setChecking] = useState(true);

    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig', siteId],
        enabled: !!siteId,
        queryFn: () => getBusinessConfigById(siteId!)
    });
    const bookingUxConfig = resolveAdminBookingUxConfig(businessConfigQuery.data);
    const resourceSingular = bookingUxConfig.terminology.resourceSingular;
    const publishingStatusQuery = useQuery({
        queryKey: ['tenant-publish-status', siteId],
        enabled: !!siteId,
        queryFn: () => getTenantPublishStatus(siteId!)
    });
    const activationMutation = useMutation({
        mutationFn: () => activateProvisionedTenant(siteId!),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-publish-status', siteId] })
    });
    const invitation = publishingStatusQuery.data?.invitation;
    const readiness = publishingStatusQuery.data?.readiness;

    useEffect(() => {
        const checkIfSiteExists = async () => {
            if (!siteId) return;

            const configDocRef = doc(db, 'booking-websites', siteId, 'config', 'businessInfo');
            const docSnap = await getDoc(configDocRef);

            setIsNewSite(!docSnap.exists());
            setChecking(false);
        };

        checkIfSiteExists();
    }, [siteId]);

    const handleEditConfigClick = () => {
        navigate(`/admin/superadmin/manage/${siteId}/businessConfig`);
    };

    const handleAddEmployeeClick = () => {
        navigate(`/admin/superadmin/manage/${siteId}/add-employee`);
    };

    const handleManageServicesClick = () => {
        navigate(`/admin/superadmin/manage/${siteId}/manage-services`);
    };

    const handleEditPublishedSiteClick = () => {
        if (!siteId) return;
        window.location.assign(`/admin/superadmin/site?tenant=${encodeURIComponent(siteId)}`);
    };

    const handleViewDashboardClick = () => {
        if (!siteId) return;
        window.location.assign(`/admin/schedule-hub?tenant=${encodeURIComponent(siteId)}`);
    };

    if (businessConfigQuery.isLoading || publishingStatusQuery.isLoading || checking) {
        return <LoadingSpinner label="Loading business" />;
    }

    return (
        <Box p={4}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Manage Site: <strong>{businessConfigQuery.data?.businessInfo.name}</strong>
                </Typography>

                {!checking && isNewSite && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This site does not currently exist in the database. A new record will be created when you save a config.
                    </Alert>
                )}

                <Typography variant="body1" gutterBottom>
                    Use the options below to manage this tenant.
                </Typography>

                {publishingStatusQuery.data?.lifecycleState === 'onboarding' && (
                    <Alert severity={readiness?.canActivate ? 'success' : 'info'} sx={{ mt: 2 }} action={
                        <Button color="inherit" size="small" disabled={activationMutation.isPending || !readiness?.canActivate}
                            onClick={() => activationMutation.mutate()}>
                            Activate
                        </Button>
                    }>
                        {readiness?.canActivate ? 'This tenant is ready to activate.' : 'Complete the launch checklist before activation.'}
                    </Alert>
                )}
                {publishingStatusQuery.data?.lifecycleState === 'onboarding' && readiness && (
                    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                        <Typography variant="h6" gutterBottom>Launch checklist</Typography>
                        <Stack spacing={0.75}>
                            <Typography>{readiness.ownerClaimed ? '✓' : '○'} Owner accepted invitation</Typography>
                            <Typography>{readiness.sitePublished ? '✓' : '○'} Website published</Typography>
                            <Typography>{readiness.subscriptionReady ? '✓' : '○'} Subscription or trial active{readiness.subscriptionBypassed ? ' (bypassed)' : ''}</Typography>
                            <Typography>{readiness.configurationReady ? '✓' : '○'} Required configuration present</Typography>
                            <Typography>{readiness.primaryHostnameReady ? '✓' : '○'} Primary hostname reserved</Typography>
                        </Stack>
                    </Paper>
                )}
                {publishingStatusQuery.data?.lifecycleState === 'onboarding' && publishingStatusQuery.data.salesPreview && siteId && (
                    <SalesPreviewCard siteId={siteId} salesPreview={publishingStatusQuery.data.salesPreview} />
                )}
                {publishingStatusQuery.data?.lifecycleState === 'onboarding' && readiness && !readiness.ownerClaimed && siteId && (
                    <OwnerInvitationCard siteId={siteId} invitation={invitation} />
                )}
                {activationMutation.isError && (
                    <Alert severity="error" sx={{ mt: 2 }}>{activationMutation.error.message}</Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                    <Button variant="contained" onClick={handleViewDashboardClick}>
                        View Tenant Dashboard
                    </Button>
                    <Button variant="contained" onClick={handleEditConfigClick}>
                        Tenant Operations
                    </Button>
                    <Button variant="contained" onClick={handleManageServicesClick}>
                        Manage Services
                    </Button>
                    <Button variant="contained" onClick={handleEditPublishedSiteClick}>
                        Edit Site
                    </Button>
                    <Button variant="contained" onClick={handleAddEmployeeClick}>
                        {`Add ${resourceSingular}`}
                    </Button>
                </Box>

                {siteId && (
                    <TenantClosureSection tenantId={siteId} businessName={businessConfigQuery.data?.businessInfo.name} />
                )}
            </Paper>
        </Box>
    );
};

export default ManageSitePage;
